import React from "react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { ChevronRightIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  HStack,
  VStack,
  Box,
} from "@whoosh/design-system/src/components/primitive";
import { Text } from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { PatronBookingAffectedRateItemLineQueryString } from "./PatronBookingAffectedRateItemLine";
import { PatronBookingAffectedRateItemLineQuery } from "./__generated__/PatronBookingAffectedRateItemLineQuery.graphql";

type PatronBookingAffectedRateItemLineInnerProps = {
  queryRef: PreloadedQuery<PatronBookingAffectedRateItemLineQuery>;
  targetId: string;
};

export const PatronBookingAffectedRateItemLineInner = ({
  queryRef,
  targetId,
}: PatronBookingAffectedRateItemLineInnerProps) => {
  const { currencySymbol } = useGetClubCurrency();
  const data = usePreloadedQuery(
    PatronBookingAffectedRateItemLineQueryString,
    queryRef
  );

  const isStandardBooking =
    data.sourceRoundOrBooking?.__typename === "StandardBooking";
  const isRound = data.sourceRoundOrBooking?.__typename === "Round";

  const isTimeSlot = data.targetSlotForBookingPrice?.__typename === "TimeSlot";

  const label = isStandardBooking
    ? "Booking Rate"
    : isRound
    ? data.sourceRoundOrBooking?.player?.fullName
    : "";

  const originalRate = {
    name: isStandardBooking
      ? data.sourceRoundOrBooking?.rate?.name ?? ""
      : isRound
      ? data.sourceRoundOrBooking?.roundRate?.rate?.name ?? ""
      : "",
    price: isStandardBooking
      ? data.sourceRoundOrBooking?.startSlot?.slotRate?.totalDurationPrice ??
        data.sourceRoundOrBooking?.startSlot?.slotRate?.totalGolfPrice ??
        0
      : isRound
      ? (data.sourceRoundOrBooking?.roundRate?.totalDurationPrice ||
          data.sourceRoundOrBooking?.roundRate?.totalGolfPrice) ??
        0
      : 0,
  };

  const newRatePrice = isTimeSlot
    ? data.targetSlotForBookingPrice?.slotRate?.totalDurationPrice ??
      data.targetSlotForBookingPrice?.slotRate?.totalGolfPrice ??
      0
    : 0;

  const newRateName = isTimeSlot
    ? data.targetSlotForBookingPrice?.slotRate?.rate?.name ?? ""
    : data.newRate?.name ?? "";

  return (
    <VStack gap={2}>
      <Text variant="mainTiny">{label}</Text>
      <HStack width="100%" alignItems="center">
        <HStack
          padding={3}
          alignItems={"center"}
          justifyContent={"space-between"}
          borderRadius="2"
          backgroundColor="surface-secondary"
          flex={1}
        >
          <VStack gap={1}>
            <Text variant="mainSmallMedium">{originalRate.name}</Text>
            <Text variant="mainSmallMedium" color="text-secondary">
              {`${currencySymbol}${ConvertCentsToDollars(originalRate.price)}`}
            </Text>
          </VStack>
          <HStack position="relative">
            <ChevronRightIcon color="#BDC7C7" />
            <Box style={{ marginLeft: -10 }}>
              <ChevronRightIcon color="#BDC7C7" />
            </Box>
          </HStack>
          <VStack gap={1}>
            <Text variant="mainSmallMedium">{newRateName}</Text>
            <Text variant="mainSmallMedium" color="text-main-primary">
              {`${currencySymbol}${ConvertCentsToDollars(newRatePrice)}`}
            </Text>
          </VStack>
        </HStack>
      </HStack>
    </VStack>
  );
};
